import { Formik } from "formik";
import { useState, type ReactNode } from "react";
import * as Yup from "yup";
import { Alert } from "@volca/ui/components/ui/alert";
import { Stack } from "@volca/ui/components/ui/stack";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import { USER_ROLES } from "@/user/constants";
import { useUpsertUser } from "@/user/query";
import type { DetailedUser } from "@/user/user";
import { WelcomeEmailNotice } from "@/user/welcome-email-notice";
import { AccountDraft } from "./account-draft";
import type { UserAccountValues } from "./types";
import { UserAccountFields } from "./user-account-fields";

const schema = Yup.object({
  first_name: Yup.string(),
  last_name: Yup.string(),
  email: Yup.string()
    .email("Enter a valid email address")
    .required("Email is required"),
  role: Yup.string().required("Role is required"),
});

interface UserAccountFormProps {
  businessId: string;
  user?: DetailedUser;
  onSaved: (user: DetailedUser, welcomeEmailFailed: boolean) => void;
  onCancel: () => void;
  draft?: UserAccountValues;
  onDraftChange?: (values: UserAccountValues | undefined) => void;
  actionsAlign?: "start" | "end";
  allowTechnicianConversion?: boolean;
  // Keep Formik outside a dialog's unmounted content so its draft survives closing.
  renderContent?: (content: ReactNode) => ReactNode;
}

export const UserAccountForm = ({
  businessId,
  user,
  onSaved,
  onCancel,
  draft,
  onDraftChange,
  actionsAlign,
  allowTechnicianConversion = false,
  renderContent = (content) => content,
}: UserAccountFormProps) => {
  const save = useUpsertUser(businessId);
  const [createdUser, setCreatedUser] = useState<DetailedUser>();
  const [failedEmailUserId, setFailedEmailUserId] = useState<string>();
  const account = user ?? createdUser;
  const [initialValues] = useState(() => ({
    first_name: account?.first_name ?? "",
    last_name: account?.last_name ?? "",
    email: account?.email ?? "",
    role: account?.role ?? "",
  }));
  if (account?.role === USER_ROLES.TECH)
    return <Alert>Manage technicians in Roster.</Alert>;
  return (
    <Formik<UserAccountValues>
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values, helpers) => {
        try {
          const result = await save.mutateAsync({ ...values, id: account?.id });
          setCreatedUser(result.user);
          if (result.welcomeEmailFailed) setFailedEmailUserId(result.user.id);
          helpers.resetForm({ values });
          onDraftChange?.(undefined);
          if (account) toast.success("Account updated");
          else if (!result.welcomeEmailFailed)
            toast.success("Account created. Welcome email sent.");
          onSaved(result.user, result.welcomeEmailFailed);
        } catch {
          // Keep the error beside the form and retain the input for retry.
        }
      }}
    >
      {() =>
        renderContent(
          <Stack gap={4} className="w-full">
            <AccountDraft draft={draft} onChange={onDraftChange} />
            {save.isError && (
              <Alert color="danger">
                Could not save the account. {getErrorMessage(save.error)}
              </Alert>
            )}
            <UserAccountFields
              account={account}
              allowTechnicianConversion={allowTechnicianConversion}
              onCancel={onCancel}
              actionsAlign={actionsAlign}
            />
            {!user && account && failedEmailUserId === account.id && (
              <WelcomeEmailNotice key={account.id} userId={account.id} failed />
            )}
          </Stack>,
        )
      }
    </Formik>
  );
};
